// T023: Implement authentication service

import { PrivateKey } from '@steempro/dsteem'
import { getSteemitClient } from './client'
import { getUserRC } from './rcService'
import { SteemitAccount } from '@/types/steemit'
import { isValidPrivateKey, isValidUsername } from '@/utils/validators'

export interface AuthResult {
  success: boolean
  username?: string
  error?: string
  rcPercentage?: number
}

/**
 * Authenticates a user with Steemit credentials
 * Verifies that the posting key matches the account's posting authority
 */
export async function authenticateUser(
  username: string,
  postingKey: string
): Promise<AuthResult> {
  try {
    // Validate inputs
    if (!isValidUsername(username)) {
      return {
        success: false,
        error: 'Invalid username format',
      }
    }

    if (!isValidPrivateKey(postingKey)) {
      return {
        success: false,
        error: 'Invalid private key format',
      }
    }

    // Verify the key is valid by attempting to create a PrivateKey object
    let privateKey: PrivateKey
    try {
      privateKey = PrivateKey.fromString(postingKey)
    } catch {
      return {
        success: false,
        error: 'Invalid private key',
      }
    }

    // Fetch account to verify it exists
    const client = getSteemitClient()
    const accounts = await client.database.getAccounts([username])

    if (!accounts || accounts.length === 0) {
      return {
        success: false,
        error: 'Account not found',
      }
    }

    const account = accounts[0]
    if (!account) {
      return {
        success: false,
        error: 'Account not found',
      }
    }

    // Verify posting key matches account's posting authority
    const publicKey = privateKey.createPublic().toString()
    const postingAuth = account.posting
    const hasPostingAuthority = postingAuth.key_auths.some(
      ([key]) => key.toString() === publicKey
    )

    if (!hasPostingAuthority) {
      return {
        success: false,
        error: 'Invalid posting key. The key does not match this account.',
      }
    }

    // Get RC percentage
    const rc = await getUserRC(username)

    return {
      success: true,
      username,
      rcPercentage: rc.percentage,
    }
  } catch (error) {
    console.error('Authentication failed:', error)
    return {
      success: false,
      error: 'Authentication failed. Please check your credentials.',
    }
  }
}

/**
 * Fetches full account data
 */
export async function getAccount(username: string): Promise<SteemitAccount | null> {
  try {
    const client = getSteemitClient()
    const accounts = await client.database.getAccounts([username])

    if (!accounts || accounts.length === 0) {
      return null
    }

    return accounts[0] as SteemitAccount
  } catch (error) {
    console.error('Failed to fetch account:', error)
    return null
  }
}
